// src/Components/modules/authorizations/Form/AuthorizationDates.js

import React from "react";
import PropTypes from "prop-types";
import { DatePicker, Form, Divider, Input, Calendar, Alert } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import dayjs from "../../../../utils/dayjs"; // Asegúrate de ajustar la ruta según tu estructura de carpetas
import "./AuthorizationDates.css"; // Asegúrate de que este archivo contiene estilos compatibles con `antd`

const AuthorizationDates = ({
  form,
  handleChange,
  handleDaySelection,
  selectedDays,
  dateRange,
  setDateRange,
  calendarStartDate,
  setCalendarStartDate,
  trasladosDateLimits, // Nuevas fechas límite de traslados
}) => {
  // Función para deshabilitar fechas en el DatePicker de fecha de inicio
  const disabledStartDate = (current) => {
    // Restricción original: no puede ser después de la fecha de fin
    const endDate = dayjs(form.final_fecha_autorizacion);
    const originalRestriction = endDate && dayjs(current).isAfter(endDate, "day");
    
    // Nueva restricción: no puede ser después de la fecha máxima de traslados
    let trasladosRestriction = false;
    if (trasladosDateLimits?.max_date) {
      const maxTrasladoDate = dayjs(trasladosDateLimits.max_date);
      trasladosRestriction = dayjs(current).isAfter(maxTrasladoDate, "day");
    }
    
    return originalRestriction || trasladosRestriction;
  };

  // Función para deshabilitar fechas en el DatePicker de fecha de fin
  const disabledEndDate = (current) => {
    // Restricción original: no puede ser antes de la fecha de inicio
    const startDate = dayjs(form.inicio_fecha_autorizacion);
    const originalRestriction = startDate && dayjs(current).isBefore(startDate, "day");
    
    // Nueva restricción: no puede ser antes de la fecha mínima de traslados
    let trasladosRestriction = false;
    if (trasladosDateLimits?.min_date) {
      const minTrasladoDate = dayjs(trasladosDateLimits.min_date);
      trasladosRestriction = dayjs(current).isBefore(minTrasladoDate, "day");
    }
    
    return originalRestriction || trasladosRestriction;
  };

  // Manejo del cambio de fecha
  const onStartDateChange = (date) => {
    const dateString = date ? date.format("YYYY-MM-DD") : null; // Cambiar undefined a null
    handleChange({
      target: {
        name: "inicio_fecha_autorizacion",
        value: dateString,
      },
    });
    setDateRange([date, dateRange[1]]);
    setCalendarStartDate(date);
  };

  const onEndDateChange = (date) => {
    const dateString = date ? date.format("YYYY-MM-DD") : null; // Cambiar undefined a null
    handleChange({
      target: {
        name: "final_fecha_autorizacion",
        value: dateString,
      },
    });
    setDateRange([dateRange[0], date]);
  };

  // Función para manejar la selección de fechas en el Calendario de `antd`
  const handleSelectDate = (value) => {
    // `value` es un objeto Dayjs de Ant Design
    handleDaySelection(value); // Pasar el objeto `dayjs` directamente
  };

  // Función para personalizar el renderizado de los días en el Calendario de `antd`
  const cellRender = (current, info) => {
    if (info.type === "date") {
      const formattedDate = current.format("YYYY-MM-DD");
      const isSelected = selectedDays.includes(formattedDate);
      return isSelected ? (
        <div className="selected-day">{current.format("DD/MM/YYYY")}</div>
      ) : null;
    }
    return null;
  };

  // Generar mensaje informativo sobre las restricciones de fecha
  const getDateRestrictionsMessage = () => {
    if (!trasladosDateLimits?.has_restrictive_traslados) return null;
    
    const messages = [];
    if (trasladosDateLimits.max_date) {
      messages.push(`Fecha de inicio máxima: ${dayjs(trasladosDateLimits.max_date).format("DD/MM/YYYY")}`);
    }
    if (trasladosDateLimits.min_date) {
      messages.push(`Fecha de fin mínima: ${dayjs(trasladosDateLimits.min_date).format("DD/MM/YYYY")}`);
    }
    
    return messages.length > 0 ? messages.join(" • ") : null;
  };

  return (
    <>
      <Divider />

      {/* Mostrar alerta informativa si hay restricciones */}
      {trasladosDateLimits?.has_restrictive_traslados && (
        <Alert
          message="Restricciones de fecha por traslados existentes"
          description={`Las fechas están limitadas por traslados en curso ${getDateRestrictionsMessage()}`}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          icon={<InfoCircleOutlined />}
        />
      )}

      {/* Inicio de Fecha de Autorización */}
      <Form.Item
        label="Inicio de Fecha de Autorización"
        style={{ marginBottom: "16px" }}
      >
        <DatePicker
          key={`start-date-${trasladosDateLimits?.max_date || 'no-limit'}`}
          name="inicio_fecha_autorizacion"
          value={
            form.inicio_fecha_autorizacion
              ? dayjs(form.inicio_fecha_autorizacion)
              : null
          }
          onChange={onStartDateChange}
          format="DD/MM/YYYY"
          placeholder="DD/MM/YYYY"
          style={{ width: "100%" }}
          disabledDate={disabledStartDate}
        />
      </Form.Item>

      {/* Final de Fecha de Autorización */}
      <Form.Item
        label="Final de Fecha de Autorización"
        style={{ marginBottom: "16px" }}
      >
        <DatePicker
          key={`end-date-${trasladosDateLimits?.min_date || 'no-limit'}`}
          name="final_fecha_autorizacion"
          value={
            form.final_fecha_autorizacion
              ? dayjs(form.final_fecha_autorizacion)
              : null
          }
          onChange={onEndDateChange}
          format="DD/MM/YYYY"
          placeholder="DD/MM/YYYY"
          style={{ width: "100%" }}
          disabledDate={disabledEndDate}
        />
      </Form.Item>

      {/* Mostrar el calendario y los días del servicio solo si el tipo de autorización es "cerrada" */}
      {form.tipo_autorizacion === "cerrada" && (
        <>
          <Form.Item label="Días del Servicio">
            <Input value={selectedDays.join(", ")} readOnly />
          </Form.Item>
          <div className="calendar-container">
            <Calendar
              onSelect={handleSelectDate} // Usar `onSelect` para Ant Design's Calendar
              value={calendarStartDate || dayjs()} // Asegurar que siempre tenga un valor válido
              fullscreen={false}
              style={{ width: "100%" }}
              cellRender={cellRender} // Personalizar el renderizado de las celdas
            />
          </div>
        </>
      )}
    </>
  );
};

AuthorizationDates.propTypes = {
  form: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleDaySelection: PropTypes.func.isRequired,
  selectedDays: PropTypes.array.isRequired,
  dateRange: PropTypes.array.isRequired, // [Dayjs, Dayjs]
  setDateRange: PropTypes.func.isRequired,
  calendarStartDate: PropTypes.object, // Dayjs object
  setCalendarStartDate: PropTypes.func.isRequired,
  trasladosDateLimits: PropTypes.shape({
    min_date: PropTypes.string, // Fecha mínima de traslados (límite para fecha fin)
    max_date: PropTypes.string, // Fecha máxima de traslados (límite para fecha inicio)
    has_restrictive_traslados: PropTypes.bool,
  }),
};

export default AuthorizationDates;
